import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	FiBookOpen,
	FiBriefcase,
	FiHome,
	FiEdit3,
	FiMail,
	FiPhone,
	FiPlus,
	FiSearch,
	FiTrash2,
	FiUsers,
	FiX,
} from "react-icons/fi";
import ContactModel, { ContactPayload } from "../../Models/ContactModel";
import { ContactsDispatch, ContactsState } from "../../Redux/OrdersContext";
import { UserContext } from "../../Redux/UserContext";
import {
	addContact,
	clearContactsError,
	deleteContact,
	fetchContacts,
	updateContact,
} from "../../Redux/slicers/contactsSlicer";
import ContactForm from "./ContactForm";
import "./Phonebook.css";

function normalizePhone(value: string): string[] {
	const digits = value.replace(/\D/g, "");
	if (!digits) return [];
	const international = digits.startsWith("0") ? `972${digits.slice(1)}` : digits;
	const local = digits.startsWith("972") ? `0${digits.slice(3)}` : digits;
	return Array.from(new Set([digits, international, local]));
}

function Phonebook(): JSX.Element {
	const context = useContext(UserContext);
	const navigate = useNavigate();
	const dispatch: ContactsDispatch = useDispatch();
	const contacts = useSelector((state: ContactsState) => state.contacts.value);
	const status = useSelector((state: ContactsState) => state.contacts.status);
	const error = useSelector((state: ContactsState) => state.contacts.error);
	const [search, setSearch] = useState("");
	const [isFormOpen, setIsFormOpen] = useState(false);
	const [editingContact, setEditingContact] = useState<ContactModel | null>(null);
	const [contactToDelete, setContactToDelete] = useState<ContactModel | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteError, setDeleteError] = useState("");

	useEffect(() => {
		if (!context.user) {
			navigate("/login");
			return;
		}
		if (status === "idle") {
			dispatch(fetchContacts());
		}
	}, [context.user, dispatch, navigate, status]);

	const visibleContacts = useMemo(() => {
		const term = search.trim().toLocaleLowerCase("he");
		const phoneTerms = normalizePhone(search);

		return contacts
			.filter((contact) => {
				if (!term) return true;
				const textFields = [
					contact.name,
					contact.email,
					contact.company,
					contact.job_description,
					contact.note,
				];
				const textMatch = textFields.some((value) =>
					(value ?? "").toLocaleLowerCase("he").includes(term),
				);
				const contactPhones = normalizePhone(contact.phone_number);
				const phoneMatch = phoneTerms.length > 0 && phoneTerms.some((queryPhone) =>
					contactPhones.some((contactPhone) => contactPhone.includes(queryPhone)),
				);
				return textMatch || phoneMatch;
			})
			.sort((first, second) => {
				const companyCompare = (first.company || "ת").localeCompare(
					second.company || "ת",
					"he",
				);
				return companyCompare || first.name.localeCompare(second.name, "he");
			});
	}, [contacts, search]);

	const openAddForm = () => {
		dispatch(clearContactsError());
		setEditingContact(null);
		setIsFormOpen(true);
	};

	const openEditForm = (contact: ContactModel) => {
		dispatch(clearContactsError());
		setEditingContact(contact);
		setIsFormOpen(true);
	};

	const saveContact = async (payload: ContactPayload) => {
		if (editingContact) {
			await dispatch(updateContact({ id: editingContact.id, contact: payload })).unwrap();
		} else {
			await dispatch(addContact(payload)).unwrap();
		}
		setIsFormOpen(false);
		setEditingContact(null);
	};

	const confirmDelete = async () => {
		if (!contactToDelete) return;
		setIsDeleting(true);
		setDeleteError("");
		try {
			await dispatch(deleteContact(contactToDelete.id)).unwrap();
			setContactToDelete(null);
		} catch (error) {
			setDeleteError(
				typeof error === "string" ? error : "מחיקת איש הקשר נכשלה. נסו שוב.",
			);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<div className="phonebook-page" dir="rtl">
			<section className="phonebook-hero">
				<div className="phonebook-hero__title">
					<span className="phonebook-hero__icon"><FiBookOpen aria-hidden="true" /></span>
					<div>
						<span className="phonebook-hero__eyebrow">האנשים שעוזרים לעסק לעבוד</span>
						<h1>ספר טלפונים</h1>
						<p>כל אנשי הקשר, החברות והמידע החשוב במקום אחד.</p>
					</div>
				</div>
				<button className="contact-button contact-button--primary phonebook-hero__add" onClick={openAddForm}>
					<FiPlus aria-hidden="true" />
					איש קשר חדש
				</button>
			</section>

			<section className="phonebook-toolbar" aria-label="כלי חיפוש">
				<label className="phonebook-search">
					<FiSearch aria-hidden="true" />
					<span className="sr-only">חיפוש אנשי קשר</span>
					<input
						type="search"
						value={search}
						onChange={(event) => setSearch(event.target.value)}
						placeholder="חיפוש לפי שם, חברה, תפקיד, טלפון, אימייל או הערה"
					/>
					{search && (
						<button type="button" onClick={() => setSearch("")} aria-label="ניקוי החיפוש">
							<FiX aria-hidden="true" />
						</button>
					)}
				</label>
				<div className="phonebook-count">
					<FiUsers aria-hidden="true" />
					<strong>{visibleContacts.length}</strong>
					<span>{search ? "תוצאות" : "אנשי קשר"}</span>
				</div>
			</section>

			{error && (
				<div className="phonebook-alert" role="alert">
					<span>{error}</span>
					<button type="button" onClick={() => dispatch(clearContactsError())} aria-label="סגירת ההודעה">
						<FiX aria-hidden="true" />
					</button>
				</div>
			)}

			{status === "loading" ? (
				<div className="phonebook-state">
					<div className="phonebook-loader" aria-hidden="true" />
					<p>טוען את ספר הטלפונים...</p>
				</div>
			) : visibleContacts.length > 0 ? (
				<section className="contact-grid" aria-label="אנשי קשר">
					{visibleContacts.map((contact) => (
						<article className="contact-card" key={contact.id}>
							<header className="contact-card__header">
								<div className="contact-card__avatar" aria-hidden="true">
									{contact.name.trim().charAt(0)}
								</div>
								<div className="contact-card__identity">
									<h2>{contact.name}</h2>
									{contact.company && (
										<p><FiHome aria-hidden="true" /> {contact.company}</p>
									)}
								</div>
								<div className="contact-card__actions">
									<button type="button" onClick={() => openEditForm(contact)} aria-label={`עריכת ${contact.name}`}>
										<FiEdit3 aria-hidden="true" />
									</button>
									<button
										type="button"
										className="contact-card__delete"
										onClick={() => {
											setDeleteError("");
											setContactToDelete(contact);
										}}
										aria-label={`מחיקת ${contact.name}`}
									>
										<FiTrash2 aria-hidden="true" />
									</button>
								</div>
							</header>

							{contact.job_description && (
								<div className="contact-card__role">
									<FiBriefcase aria-hidden="true" />
									<span>{contact.job_description}</span>
								</div>
							)}

							<div className="contact-card__links">
								<a href={`tel:${contact.phone_number}`} dir="ltr">
									<FiPhone aria-hidden="true" />
									<span>{contact.phone_number}</span>
								</a>
								{contact.email && (
									<a href={`mailto:${contact.email}`} dir="ltr">
										<FiMail aria-hidden="true" />
										<span>{contact.email}</span>
									</a>
								)}
							</div>

							{contact.note && <p className="contact-card__note">{contact.note}</p>}
						</article>
					))}
				</section>
			) : (
				<div className="phonebook-state phonebook-state--empty">
					<span><FiUsers aria-hidden="true" /></span>
					<h2>{search ? "לא מצאנו אנשי קשר מתאימים" : "ספר הטלפונים עדיין ריק"}</h2>
					<p>
						{search
							? "נסו לחפש בשם אחר, חברה, תפקיד או מספר טלפון."
							: "הוסיפו את איש הקשר הראשון כדי שכל המידע יהיה תמיד בהישג יד."}
					</p>
					{!search && (
						<button className="contact-button contact-button--primary" onClick={openAddForm}>
							<FiPlus aria-hidden="true" />
							הוספת איש קשר ראשון
						</button>
					)}
				</div>
			)}

			{isFormOpen && (
				<ContactForm
					contact={editingContact}
					onClose={() => setIsFormOpen(false)}
					onSave={saveContact}
				/>
			)}

			{contactToDelete && (
				<div className="contact-dialog-backdrop" role="presentation" onMouseDown={() => setContactToDelete(null)}>
					<section
						className="delete-dialog"
						role="alertdialog"
						aria-modal="true"
						aria-labelledby="delete-contact-title"
						onMouseDown={(event) => event.stopPropagation()}
					>
						<span className="delete-dialog__icon"><FiTrash2 aria-hidden="true" /></span>
						<h2 id="delete-contact-title">מחיקת איש קשר</h2>
						<p>
							האם למחוק את <strong>{contactToDelete.name}</strong>
							{contactToDelete.company ? ` מחברת ${contactToDelete.company}` : ""}?
						</p>
						<small>לא ניתן לבטל פעולה זו.</small>
						{deleteError && <p className="delete-dialog__error" role="alert">{deleteError}</p>}
						<div className="delete-dialog__actions">
							<button
								type="button"
								className="contact-button contact-button--secondary"
								onClick={() => setContactToDelete(null)}
								disabled={isDeleting}
							>
								ביטול
							</button>
							<button
								type="button"
								className="contact-button contact-button--danger"
								onClick={confirmDelete}
								disabled={isDeleting}
							>
								<FiTrash2 aria-hidden="true" />
								{isDeleting ? "מוחק..." : "מחיקה"}
							</button>
						</div>
					</section>
				</div>
			)}
		</div>
	);
}

export default Phonebook;
